/**
 * Shared image validation constants and helper for F-002 ImageUpload
 * (single-file store logo) and F-003 MultiImageUpload (up to 5 product
 * images). Product images intentionally have their own size ceiling so
 * merchant product photography can be more generous without changing
 * logo/category media limits.
 *
 * All constants are framework-free so both admin and storefront can
 * import them without pulling React.
 */

/** Maximum generic image size in bytes. 2 MiB matches logo/category buckets. */
export const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

/** Human-friendly generic max size used in i18n strings (`2 MB`). */
export const MAX_IMAGE_MB = 2;

/** Maximum product image size in bytes. Mirrors the product-images bucket. */
export const MAX_PRODUCT_IMAGE_BYTES = 4 * 1024 * 1024;

/** Human-friendly product image max size used in i18n strings (`4 MB`). */
export const MAX_PRODUCT_IMAGE_MB = 4;

/** Immutable set of allowed MIME types. ReadonlySet prevents mutation
 *  from callers. */
export const ACCEPTED_IMAGE_MIME: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

/** Comma-joined MIME list for `<input accept="...">`. */
export const ACCEPTED_IMAGE_MIME_LIST = 'image/jpeg,image/png,image/webp';

/** Maximum number of images a single product can have. Mirrors the DB
 *  `product_images_enforce_max_per_product_trg` trigger (C4). */
export const MAX_PRODUCT_IMAGES = 5;

/** Whitelist of validation errors `validateImageFile` is allowed to
 *  return. Both F-002 (`admin.createStore.errors.logo*`) and F-003
 *  (`admin.products.errors.image*`) map these generic codes to their
 *  own translation keys. */
export type ImageValidationError = 'imageTooLarge' | 'imageInvalidType';

/**
 * Validate a single File against the shared size + MIME rules.
 * Returns null when the file is acceptable.
 *
 * Callers map the returned code to a locale-specific translation key.
 */
function validateAcceptedImageFile(file: File, maxBytes: number): ImageValidationError | null {
  if (!ACCEPTED_IMAGE_MIME.has(file.type)) {
    return 'imageInvalidType';
  }
  if (file.size > maxBytes) {
    return 'imageTooLarge';
  }
  return null;
}

export function validateImageFile(file: File): ImageValidationError | null {
  return validateAcceptedImageFile(file, MAX_IMAGE_BYTES);
}

export function validateProductImageFile(file: File): ImageValidationError | null {
  return validateAcceptedImageFile(file, MAX_PRODUCT_IMAGE_BYTES);
}

/** Map validated MIME to the file extension used in storage paths.
 *  Deliberately only emits `jpg`/`png`/`webp` — the DB CHECK regex
 *  (`storage_path`) rejects any other extension, and dropping `jpeg`
 *  is the Y1 convention. */
export function getFileExtension(mimeType: string): 'jpg' | 'png' | 'webp' {
  switch (mimeType) {
    case 'image/jpeg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/webp':
      return 'webp';
    default:
      // Unreachable if the caller pre-validated via validateImageFile.
      // Fallback kept for defence in depth; the storage path CHECK will
      // reject anything non-conforming anyway.
      return 'png';
  }
}
